import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './LibraryHeader.css';
import { DataContext } from "../DataContexts/DataContext";
import BrowsePage from './BrowsePage';
import TagManager from '../TagManager/TagManager';
import TrackUpload from '../TrackUpload/TrackUpload';
import RequestsPanel from '../Inbox/RequestsPanel';
import DashboardSection from '../components/DashboardSection';
import useDeepLinkedSection from '../navigation/useDeepLinkedSection';

const TABS = ['browse', 'upload', 'tags'];

// DJB-91: the old "Artist Search" and "Songs" tabs are merged into the single
// "Browse" sub-page (BrowsePage). DJB-268: Tags is its own sub-page again.
const LibraryHeader = () => {
  const { loading, error } = useContext(DataContext);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'browse'
  );

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (TABS.includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // DJB-159: the Upload section (Upload sub-page) and the Tags section (Tags
  // sub-page, DJB-268) sit on the Dashboard's borderless collapsible shell.
  // Both start expanded (they were always visible before) and collapse
  // independently. DJB-269: the Song Requests section under Upload does too.
  const [openSections, setOpenSections] = useState(() => new Set(['upload', 'songRequests', 'tags']));

  const openSection = (key) =>
    setOpenSections((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));

  const toggleSection = (key) =>
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  // DJB-112: a section picked from the left nav arrives as ?section=<anchor>
  // and must open even if the user collapsed it
  useDeepLinkedSection(
    {
      'section-upload': 'upload',
      'section-song-requests': 'songRequests',
      'section-tags': 'tags',
    },
    openSection
  );

  if (loading) {
    return (
      <div>
        <h1>Library</h1>
        <p>Loading data, please wait... 🎵</p>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h1>Library</h1>
        <p>Error loading data: {error}</p>
      </div>
    );
  }

  return (
    <div className="library-navigator-header">
      <div className="tab-container">
        <button
          className={`tab ${activeTab === 'browse' ? 'active' : ''}`}
          onClick={() => setActiveTab('browse')}
        >
          Browse
        </button>
        <button
          className={`tab ${activeTab === 'upload' ? 'active' : ''}`}
          onClick={() => setActiveTab('upload')}
        >
          Upload
        </button>
        <button
          className={`tab ${activeTab === 'tags' ? 'active' : ''}`}
          onClick={() => setActiveTab('tags')}
        >
          Tags
        </button>
      </div>
      <div className="tab-content">
        {activeTab === 'browse' && (
          <div>
            <BrowsePage></BrowsePage>
          </div>
        )}
        {activeTab === 'upload' && (
          <div className="upload-page">
            <DashboardSection
              id="section-upload"
              title="Upload"
              isOpen={openSections.has('upload')}
              onToggle={() => toggleSection('upload')}
              bodyId="dashboard-section-body-upload"
            >
              <TrackUpload></TrackUpload>
            </DashboardSection>
            {/* DJB-269: the open song requests right under the upload, so
                whoever uploads can handle them on the same page. The very
                same panel as Inbox › Song Requests (filter, day groups,
                cards, match panel, actions); its actions update the nav
                badges through InboxCountsContext like the inbox page. */}
            <DashboardSection
              id="section-song-requests"
              title="Song Requests"
              isOpen={openSections.has('songRequests')}
              onToggle={() => toggleSection('songRequests')}
              bodyId="dashboard-section-body-song-requests"
            >
              <RequestsPanel kind="songs" />
            </DashboardSection>
          </div>
        )}
        {activeTab === 'tags' && (
          // DJB-268: Tags moved out from under Upload (DJB-100) onto its own
          // sub-page, on the same section shell and page container
          <div className="tags-page">
            <DashboardSection
              id="section-tags"
              title="Tags"
              isOpen={openSections.has('tags')}
              onToggle={() => toggleSection('tags')}
              bodyId="dashboard-section-body-tags"
            >
              <TagManager></TagManager>
            </DashboardSection>
          </div>
        )}
      </div>
    </div>
  );
};

export default LibraryHeader;
